/**
 * Money arrives as an integer of minor units and is formatted once, here.
 *
 * Dividing by 100 in a component is how one screen ends up saying €99.00 and
 * another €99 for the same plan — and how a cent goes missing in a rounding
 * nobody reviewed. `Intl` does the currency and the locale; this decides only
 * where the decimal point goes.
 */

export function formatMoney(cents, currency = 'EUR') {
  if (cents === null || cents === undefined) return '—';
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency,
    // A price is never shown with trailing zeros it does not need: €99, but
    // €99.50 when there is a half.
    minimumFractionDigits: cents % 100 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(cents / 100);
}

/** "€99 a month", or "€990 a year". */
export function formatPrice(cents, currency = 'EUR', interval = 'MONTHLY') {
  if (cents === 0) return 'Free';
  const period = interval === 'YEARLY' ? 'a year' : 'a month';
  return `${formatMoney(cents, currency)} ${period}`;
}

/** `null` is unlimited, and reads as such. Zero would mean none at all. */
export function formatLimit(value, noun) {
  if (value === null || value === undefined) return `Unlimited ${noun}`;
  return `${value} ${value === 1 ? noun.replace(/s$/, '') : noun}`;
}

/**
 * What a plan grants, in the order a company cares about it.
 *
 * Every line here is enforced by the backend. Nothing is listed that nothing
 * checks — a feature bullet no code honours is a lie on a pricing page.
 */
export function entitlementLines(entitlements) {
  if (!entitlements) return [];
  return [
    formatLimit(entitlements.max_active_internships, 'live internships'),
    formatLimit(entitlements.max_team_members, 'team members'),
    `${entitlements.analytics_history_days} days of analytics history`,
    entitlements.candidate_sourcing
      ? 'Find candidates who have not applied'
      : 'Applicants only — no candidate sourcing',
    entitlements.ats_integration
      ? 'Candidates sent to your ATS automatically'
      : 'Export candidates to your ATS by hand',
  ];
}

/**
 * The same promises as `entitlementLines`, short enough for a table cell.
 *
 * Only the three that carry a number: the two boolean features are drawn as
 * badges beside this, because "no candidate sourcing" costs a whole column to
 * say nothing.
 */
export function entitlementSummary(entitlements) {
  if (!entitlements) return '';
  // `1 listings` in a table of plans reads as a typo, and the free plan is
  // exactly the row where the number is 1.
  const limit = (value, noun) =>
    value == null ? `Unlimited ${noun}s` : `${value} ${noun}${value === 1 ? '' : 's'}`;
  return [
    limit(entitlements.max_active_internships, 'listing'),
    limit(entitlements.max_team_members, 'seat'),
    `${entitlements.analytics_history_days}d analytics`,
  ].join(' · ');
}

/** What a student plan grants each month. `null` is unlimited. */
export function studentEntitlementLines(allowances) {
  if (!allowances) return [];
  const monthly = (value, noun) =>
    value == null ? `Unlimited ${noun}` : `${value} ${noun} a month`;
  return [
    monthly(allowances.ai_cv_reviews_per_month, 'AI CV reviews'),
    monthly(allowances.ai_roadmaps_per_month, 'AI career roadmaps'),
    monthly(allowances.applications_per_month, 'applications'),
    allowances.application_boost
      ? 'Applications marked Premium to companies'
      : 'No Premium badge on applications',
  ];
}

/** The short, table-cell version of `studentEntitlementLines`. */
export function studentEntitlementSummary(allowances) {
  if (!allowances) return '';
  const limit = (value, noun) => (value == null ? `Unlimited ${noun}` : `${value} ${noun}`);
  return [
    limit(allowances.ai_cv_reviews_per_month, 'CV reviews'),
    limit(allowances.ai_roadmaps_per_month, 'roadmaps'),
    limit(allowances.applications_per_month, 'applications'),
  ].join(' · ');
}

export function isStudentPlan(plan) {
  return plan?.audience === 'STUDENT';
}

/** The grant lines for either audience. */
export function planGrantLines(plan) {
  return isStudentPlan(plan)
    ? studentEntitlementLines(plan.student_entitlements)
    : entitlementLines(plan?.entitlements);
}

/** The one-line grant summary for either audience. */
export function planGrantSummary(plan) {
  return isStudentPlan(plan)
    ? studentEntitlementSummary(plan.student_entitlements)
    : entitlementSummary(plan?.entitlements);
}

const STATUS_TONES = {
  ACTIVE: 'success',
  TRIALING: 'info',
  PAST_DUE: 'warning',
  CANCELED: 'neutral',
};

const STATUS_LABELS = {
  ACTIVE: 'Active',
  TRIALING: 'Trial',
  PAST_DUE: 'Payment overdue',
  CANCELED: 'Ended',
};

export function statusTone(status) {
  return STATUS_TONES[status] ?? 'neutral';
}

export function statusLabel(status) {
  return STATUS_LABELS[status] ?? 'Free';
}

const INVOICE_TONES = {
  OPEN: 'warning',
  PAID: 'success',
  VOID: 'neutral',
};

export function invoiceTone(invoice) {
  if (invoice.is_overdue) return 'danger';
  return INVOICE_TONES[invoice.status] ?? 'neutral';
}

export function invoiceLabel(invoice) {
  if (invoice.is_overdue) return 'Overdue';
  return { OPEN: 'Due', PAID: 'Paid', VOID: 'Voided' }[invoice.status] ?? invoice.status;
}

/**
 * How close a company is to a limit, as a percentage, or `null` when there is
 * no limit to be close to.
 */
export function usageRatio(used, limit) {
  if (limit === null || limit === undefined || limit === 0) return null;
  return Math.min(100, Math.round((used / limit) * 100));
}

/** The currencies a plan or a fixed-amount coupon can be priced in. */
export const BILLING_CURRENCIES = ['INR', 'EUR', 'USD', 'GBP'];

/**
 * Totals kept apart by currency, as one line: "€943.32 + ₹8.33". Money in two
 * currencies is never added together. Nothing owed reads as `zero` in
 * `fallbackCurrency`.
 */
export function formatAmounts(byCurrency, fallbackCurrency = 'EUR') {
  const entries = Object.entries(byCurrency ?? {}).filter(([, cents]) => cents);
  if (entries.length === 0) return formatMoney(0, fallbackCurrency);
  // The fallback currency first, the rest alphabetically, so the order is
  // stable from one load to the next.
  entries.sort(([a], [b]) =>
    a === fallbackCurrency ? -1 : b === fallbackCurrency ? 1 : a.localeCompare(b),
  );
  return entries.map(([currency, cents]) => formatMoney(cents, currency)).join(' + ');
}
